import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import axios from "axios";
import {
    Alert,
    Button,
    Spinner
} from "react-bootstrap";
import PropTypes from "prop-types";

import ShopsContext from "../../../context/ShopsContext";
import Loading from "../../../components/Loading";
import RadioChip from "../../../components/RadioChip";
import DiscountCodeProductListItem from "./components/DiscountCodeProductListItem";
import DiscountCodeAddProductModal from "./modal/DiscountCodeAddProductModal";
import DiscountCodeRemoveProductModal from "./modal/DiscountCodeRemoveProductModal";

function DiscountCodeDetailProducts({ discountCode, setDiscountCode }) {
    const shopsContext = useContext(ShopsContext);

    const [eligibleProducts, setEligibleProducts] = useState(null);
    const [requiredProducts, setRequiredProducts] = useState(null);
    const [allProductsEligible, setAllProductsEligible] = useState(true);
    const [requiredProductsAll, setRequiredProductsAll] = useState(null);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [showAddEligibleModal, setShowAddEligibleModal] = useState(false);
    const [showRemoveEligibleModal, setShowRemoveEligibleModal] = useState(false);
    const [removeEligibleProduct, setRemoveEligibleProduct] = useState(null);

    const [showAddRequiredModal, setShowAddRequiredModal] = useState(false);
    const [showRemoveRequiredModal, setShowRemoveRequiredModal] = useState(false);
    const [removeRequiredProduct, setRemoveRequiredProduct] = useState(null);

    useEffect(() => {
        if(discountCode) {
            setEligibleProducts(discountCode.eligibleProducts || []);
            setRequiredProducts(discountCode.requiredProducts || []);
            setAllProductsEligible(discountCode.allProductsEligible);
            setRequiredProductsAll(discountCode.requiredProductsAll);
        }
    }, [discountCode]);

    const hasChanges = useMemo(() => {
        if(!discountCode) return false;
        if(allProductsEligible !== discountCode.allProductsEligible) return true;
        if(requiredProductsAll !== discountCode.requiredProductsAll) return true;
        const origEligibleIds = (discountCode.eligibleProducts || []).map(product => product.id).sort().join(",");
        const currEligibleIds = (eligibleProducts || []).map(product => product.id).sort().join(",");
        if(origEligibleIds !== currEligibleIds) return true;
        const origRequiredIds = (discountCode.requiredProducts || []).map(product => product.id).sort().join(",");
        const currRequiredIds = (requiredProducts || []).map(product => product.id).sort().join(",");
        if(origRequiredIds !== currRequiredIds) return true;
        return false;
    }, [discountCode, allProductsEligible, requiredProductsAll, eligibleProducts, requiredProducts]);

    // eslint-disable-next-line complexity
    const handleSave = useCallback(() => {
        setError(null);
        setSuccess(null);
        setSaving(true);
        axios.post("/updateDiscountCode", {
            shopId: shopsContext.currentShop.id,
            discountCodeId: discountCode.id,
            code: discountCode.code,
            type: discountCode.type,
            discountValue: discountCode.discountValue !== null ? discountCode.discountValue : "null",
            discountPercentage: discountCode.discountPercentage !== null ? discountCode.discountPercentage : "null",
            maxUsageCount: discountCode.maxUsageValue !== null ? discountCode.maxUsageValue : "null",
            startDate: discountCode.startDate || "",
            expirationDate: discountCode.expirationDate || "",
            enabled: discountCode.enabled ? 1 : 0,
            allProductsEligible: allProductsEligible ? 1 : 0,
            eligibleProductIds: JSON.stringify(allProductsEligible ? [] : (eligibleProducts || []).map(product => product.id)),
            requiredProductsAll: requiredProductsAll !== null ? (requiredProductsAll ? 1 : 0) : "null",
            requiredProductIds: JSON.stringify(requiredProductsAll !== null ? (requiredProducts || []).map(product => product.id) : []),
        })
            .then((response) => {
                setSaving(false);
                if(response.data.valid) {
                    setDiscountCode(response.data.discountCode);
                    setSuccess("Wijzigingen opgeslagen!");
                } else {
                    setError("Er ging iets fout. Probeer het later opnieuw. (" + response.data.error + ")");
                }
            })
            .catch(() => {
                setSaving(false);
                setError("Er ging iets fout. Probeer het later opnieuw.");
            });
    }, [shopsContext.currentShop.id, discountCode, setDiscountCode, allProductsEligible, eligibleProducts, requiredProductsAll, requiredProducts]);

    const handleAddEligibleProduct = useCallback((productObject, onComplete) => {
        if(!productObject) return;
        const allProducts = eligibleProducts || [];
        const existingProduct = allProducts.find(product => product.id === productObject.id);
        if(!existingProduct) {
            setEligibleProducts([...allProducts, productObject]);
        }
        setSuccess(null);
        if(onComplete) onComplete();
    }, [eligibleProducts]);

    const handleRemoveEligibleProduct = useCallback((productId, onComplete) => {
        setEligibleProducts(prev => (prev || []).filter(product => product.id !== productId));
        setSuccess(null);
        if(onComplete) onComplete();
    }, []);

    const handleAddRequiredProduct = useCallback((productObject, onComplete) => {
        if(!productObject) return;
        const allProducts = requiredProducts || [];
        const existingProduct = allProducts.find(product => product.id === productObject.id);
        if(!existingProduct) {
            setRequiredProducts([...allProducts, productObject]);
        }
        setSuccess(null);
        if(onComplete) onComplete();
    }, [requiredProducts]);

    const handleRemoveRequiredProduct = useCallback((productId, onComplete) => {
        setRequiredProducts(prev => (prev || []).filter(product => product.id !== productId));
        setSuccess(null);
        if(onComplete) onComplete();
    }, []);

    if(!discountCode) {
        return (
            <Loading/>
        )
    }

    return (
        <React.Fragment>
            <DiscountCodeAddProductModal
                show={ showAddEligibleModal }
                handleClose={ () => setShowAddEligibleModal(false) }
                existingProducts={ eligibleProducts || [] }
                onSave={ handleAddEligibleProduct }
            />
            <DiscountCodeRemoveProductModal
                show={ showRemoveEligibleModal }
                handleClose={ () => setShowRemoveEligibleModal(false) }
                product={ removeEligibleProduct }
                onRemove={ handleRemoveEligibleProduct }
            />
            <DiscountCodeAddProductModal
                show={ showAddRequiredModal }
                handleClose={ () => setShowAddRequiredModal(false) }
                existingProducts={ requiredProducts || [] }
                onSave={ handleAddRequiredProduct }
            />
            <DiscountCodeRemoveProductModal
                show={ showRemoveRequiredModal }
                handleClose={ () => setShowRemoveRequiredModal(false) }
                product={ removeRequiredProduct }
                onRemove={ handleRemoveRequiredProduct }
            />

            { error && (
                <Alert variant="danger">{ error }</Alert>
            )}
            { success && (
                <Alert variant="success">{ success }</Alert>
            )}

            <div className="card mb-3">
                <div className="card-body">
                    <h4>Geldige producten</h4>
                    <div className="custom-control custom-checkbox mb-3">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id="allProductsEligible"
                            checked={ allProductsEligible }
                            onChange={ (event) => {
                                setAllProductsEligible(event.target.checked);
                                setSuccess(null);
                            }}
                            disabled={ saving }
                        />
                        <label htmlFor="allProductsEligible" className="custom-control-label">Alle producten</label>
                        <small className="form-text text-muted">
                            Wanneer ingeschakeld is de kortingscode geldig voor alle producten.
                        </small>
                    </div>
                    { !allProductsEligible && (
                        <React.Fragment>
                            { eligibleProducts && eligibleProducts.length === 0 ? (
                                <div className="text-center mb-3">
                                    <h1><i className="fas fa-shopping-basket"/></h1>
                                    <h4>Geen producten</h4>
                                    <p>Er zijn nog geen geldige producten geselecteerd.</p>
                                    <Button variant="primary" onClick={ () => setShowAddEligibleModal(true) } disabled={ saving }>
                                        <i className="fas fa-plus mr-2"/>
                                        Eerste product toevoegen
                                    </Button>
                                </div>
                            ) : (
                                <React.Fragment>
                                    <Button variant="secondary" size="sm" className="mb-2" onClick={ () => setShowAddEligibleModal(true) } disabled={ saving }>
                                        <i className="fas fa-plus mr-2"/>
                                        Product toevoegen
                                    </Button>
                                    { eligibleProducts && eligibleProducts.map((product) => (
                                        <div key={ product.id }>
                                            <DiscountCodeProductListItem
                                                product={ product }
                                                disabled={ saving }
                                                onClickRemove={ (removedProduct) => {
                                                    setRemoveEligibleProduct(removedProduct);
                                                    setShowRemoveEligibleModal(true);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-body">
                    <h4>Vereiste producten</h4>
                    <div className="custom-control custom-checkbox mb-3">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id="enableRequiredProducts"
                            checked={ requiredProductsAll !== null }
                            onChange={ (event) => {
                                if(event.target.checked) {
                                    setRequiredProductsAll(false);
                                } else {
                                    setRequiredProductsAll(null);
                                    setRequiredProducts([]);
                                }
                                setSuccess(null);
                            }}
                            disabled={ saving }
                        />
                        <label htmlFor="enableRequiredProducts" className="custom-control-label">Vereiste producten instellen</label>
                        <small className="form-text text-muted">
                            Wanneer ingeschakeld moet de klant bepaalde producten in de winkelwagen hebben om de kortingscode te kunnen gebruiken.
                        </small>
                    </div>
                    { requiredProductsAll !== null && (
                        <React.Fragment>
                            <div className="mb-3">
                                <RadioChip
                                    checked={ requiredProductsAll === false }
                                    onClick={ () => { setRequiredProductsAll(false); setSuccess(null); } }
                                    className="mr-2"
                                    disabled={ saving }
                                >
                                    Minstens een vereist product
                                </RadioChip>
                                <RadioChip
                                    checked={ requiredProductsAll === true }
                                    onClick={ () => { setRequiredProductsAll(true); setSuccess(null); } }
                                    disabled={ saving }
                                >
                                    Alle vereiste producten
                                </RadioChip>
                            </div>
                            { requiredProducts && requiredProducts.length === 0 ? (
                                <div className="text-center mb-3">
                                    <h1><i className="fas fa-shopping-basket"/></h1>
                                    <h4>Geen producten</h4>
                                    <p>Er zijn nog geen vereiste producten geselecteerd.</p>
                                    <Button variant="primary" onClick={ () => setShowAddRequiredModal(true) } disabled={ saving }>
                                        <i className="fas fa-plus mr-2"/>
                                        Eerste product toevoegen
                                    </Button>
                                </div>
                            ) : (
                                <React.Fragment>
                                    <Button variant="secondary" size="sm" className="mb-2" onClick={ () => setShowAddRequiredModal(true) } disabled={ saving }>
                                        <i className="fas fa-plus mr-2"/>
                                        Product toevoegen
                                    </Button>
                                    { requiredProducts && requiredProducts.map((product) => (
                                        <div key={ product.id }>
                                            <DiscountCodeProductListItem
                                                product={ product }
                                                disabled={ saving }
                                                onClickRemove={ (removedProduct) => {
                                                    setRemoveRequiredProduct(removedProduct);
                                                    setShowRemoveRequiredModal(true);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </div>
            </div>

            <Button variant="primary" onClick={ handleSave } disabled={ saving || !hasChanges }>
                { saving && (
                    <Spinner animation="border" variant="light" size="sm" className="mr-2"/>
                )}
                Opslaan
            </Button>
        </React.Fragment>
    )
}
DiscountCodeDetailProducts.propTypes = {
    discountCode: PropTypes.object,
    setDiscountCode: PropTypes.func.isRequired,
}

export default React.memo(DiscountCodeDetailProducts);
